"use client"

import { useState, useEffect } from "react"
import { PROFILES } from "./data"
import ConceptSelection from "./ConceptSelection"
import Customization from "./Customization"
import CostDashboard from "./CostDashboard"
import CostDistribution from "./CostDistribution"
import ItemizedBreakdown from "./ItemizedBreakdown"

export default function RestaurantStartupCostCalculator() {
   // State variables
   const [activeConcept, setActiveConcept] = useState<string>("food-truck")
   const [activeMarketLevel, setActiveMarketLevel] = useState<"low" | "medium" | "high">("medium")
   const [sqFt, setSqFt] = useState<number>(200)
   const [secondGen, setSecondGen] = useState<boolean>(false)
   const [usedEquip, setUsedEquip] = useState<boolean>(false)
   const [alcohol, setAlcohol] = useState<boolean>(true)

   // Financial inputs
   const [termYears, setTermYears] = useState<number>(8)
   const [interestRate, setInterestRate] = useState<number>(9)
   const [ticketSize, setTicketSize] = useState<number>(15)

   // Costs mapping state
   const [workingCosts, setWorkingCosts] = useState<Record<string, number>>({})

   // Accordion open state
   const [openAccordions, setOpenAccordions] = useState<Record<string, boolean>>({ space: true })

   // Recalculate baseline cost allocations whenever controls change
   useEffect(() => {
      const profile = PROFILES[activeConcept]
      let scalingMultiplier = 1.0

      if (activeConcept !== "food-truck") {
         scalingMultiplier = sqFt / profile.defaultSqFt
      }

      let tierMultiplier = 1.0
      if (activeMarketLevel === "low") tierMultiplier = 0.75
      if (activeMarketLevel === "high") tierMultiplier = 1.45

      let vehicleCost = Math.round(profile.space * 0.75 * scalingMultiplier * tierMultiplier)
      if (secondGen && activeConcept !== "food-truck") {
         vehicleCost = Math.round(vehicleCost * 0.4)
      }

      const calculatedCosts: Record<string, number> = {
         vehicle: vehicleCost,
         wrap: Math.round(profile.space * 0.1 * tierMultiplier),
         generator: Math.round(profile.space * 0.15 * tierMultiplier),

         cooking: Math.round(profile.equipment * 0.35 * scalingMultiplier * tierMultiplier * (usedEquip ? 0.55 : 1.0)),
         refrigeration: Math.round(
            profile.equipment * 0.25 * scalingMultiplier * tierMultiplier * (usedEquip ? 0.55 : 1.0),
         ),
         smallwares: Math.round(profile.equipment * 0.1 * tierMultiplier),
         dishwashing: Math.round(
            profile.equipment * 0.15 * scalingMultiplier * tierMultiplier * (usedEquip ? 0.55 : 1.0),
         ),
         ventilation: Math.round(profile.equipment * 0.15 * scalingMultiplier * tierMultiplier),

         furniture: Math.round(profile.foh * 0.35 * scalingMultiplier * tierMultiplier),
         pos: Math.round(profile.foh * 0.25 * tierMultiplier),
         bar_equip: Math.round(profile.foh * 0.25 * tierMultiplier),
         decor: Math.round(profile.foh * 0.15 * tierMultiplier),

         biz_license: Math.round(profile.licensing * 0.2 * tierMultiplier),
         food_license: Math.round(profile.licensing * 0.25 * tierMultiplier),
         liquor_license: alcohol ? Math.round(profile.licensing * 0.35 * tierMultiplier) : 0,
         safety_permits: Math.round(profile.licensing * 0.1 * tierMultiplier),
         legal_cpa: Math.round(profile.licensing * 0.1 * tierMultiplier),

         raw_f_b: Math.round(profile.inventory * 0.5 * tierMultiplier),
         alcohol_stock: alcohol ? Math.round(profile.inventory * 0.25 * tierMultiplier) : 0,
         supplies: Math.round(profile.inventory * 0.15 * tierMultiplier),
         cleaning_chem: Math.round(profile.inventory * 0.1 * tierMultiplier),

         branding: Math.round(profile.marketing * 0.15 * tierMultiplier),
         website: Math.round(profile.marketing * 0.35 * tierMultiplier),
         opening_event: Math.round(profile.marketing * 0.2 * tierMultiplier),
         advertising: Math.round(profile.marketing * 0.3 * tierMultiplier),

         rent_buffer: Math.round(profile.working * 0.3 * tierMultiplier),
         payroll_reserve: Math.round(profile.working * 0.4 * tierMultiplier),
         utilities: Math.round(profile.working * 0.05 * tierMultiplier),
         insurance_annual: Math.round(profile.working * 0.1 * tierMultiplier),
         emergency_buffer: Math.round(profile.working * 0.15 * tierMultiplier),
      }

      setWorkingCosts(calculatedCosts)
   }, [activeConcept, activeMarketLevel, sqFt, secondGen, usedEquip, alcohol])

   // Handle switching concept profile
   const handleConceptChange = (conceptKey: string) => {
      setActiveConcept(conceptKey)
      const profile = PROFILES[conceptKey]
      setSqFt(profile.defaultSqFt)
      setTicketSize(profile.ticket)
      if (conceptKey === "food-truck") {
         setSecondGen(false)
      }
   }

   // Helper calculation totals
   const totalStartupCost = Object.values(workingCosts).reduce((a, b) => a + b, 0)

   // Loan and Break-even Calculations
   const decimalAPR = (isNaN(interestRate) || interestRate < 0 ? 9 : interestRate) / 100
   const totalInterest = Math.round(totalStartupCost * decimalAPR * termYears)
   const totalRepayment = totalStartupCost + totalInterest
   const monthlyPayment = Math.round(totalRepayment / (termYears * 12)) || 1

   const validTicket = isNaN(ticketSize) || ticketSize <= 0 ? 15 : ticketSize
   const dailyOverhead = Math.round((monthlyPayment * 1.7) / 30)
   const customersNeeded = Math.ceil(dailyOverhead / validTicket) || 1

   // Handlers for manual line-item inputs
   const handleItemCostChange = (id: string, val: string) => {
      const num = parseInt(val, 10) || 0
      setWorkingCosts((prev) => ({ ...prev, [id]: num }))
   }

   const toggleAccordion = (catKey: string) => {
      setOpenAccordions((prev) => ({ ...prev, [catKey]: !prev[catKey] }))
   }

   const resetAll = () => {
      setSecondGen(false)
      setUsedEquip(false)
      setAlcohol(true)
      setTermYears(8)
      setInterestRate(9)
      setActiveMarketLevel("medium")
      handleConceptChange("food-truck")
   }

   return (
      <div className="w-full max-w-full overflow-x-hidden px-4 py-6 md:p-8">
         <ConceptSelection activeConcept={activeConcept} onConceptChange={handleConceptChange} />

         <Customization
            activeConcept={activeConcept}
            sqFt={sqFt}
            onSqFtChange={setSqFt}
            activeMarketLevel={activeMarketLevel}
            onMarketLevelChange={setActiveMarketLevel}
            secondGen={secondGen}
            onSecondGenChange={setSecondGen}
            usedEquip={usedEquip}
            onUsedEquipChange={setUsedEquip}
            alcohol={alcohol}
            onAlcoholChange={setAlcohol}
         />

         <CostDashboard
            activeConcept={activeConcept}
            activeMarketLevel={activeMarketLevel}
            totalStartupCost={totalStartupCost}
            termYears={termYears}
            onTermYearsChange={setTermYears}
            interestRate={interestRate}
            onInterestRateChange={setInterestRate}
            ticketSize={ticketSize}
            onTicketSizeChange={setTicketSize}
            totalInterest={totalInterest}
            totalRepayment={totalRepayment}
            monthlyPayment={monthlyPayment}
            dailyOverhead={dailyOverhead}
            customersNeeded={customersNeeded}
            validTicket={validTicket}
            onReset={resetAll}
         />

         <CostDistribution workingCosts={workingCosts} totalStartupCost={totalStartupCost} />

         <ItemizedBreakdown
            workingCosts={workingCosts}
            openAccordions={openAccordions}
            onToggleAccordion={toggleAccordion}
            onItemCostChange={handleItemCostChange}
         />
      </div>
   )
}
